import { useEffect, useRef, useState } from 'react';
import { scrollToHomeSection } from '@/utils/scrollToHomeSection';

const VIDEO_SRC =
  'https://storage.helloreaddy.io/project_files/c29fa704-fec6-4111-a9ea-1115c92df55d/e7b25d8a-3fcd-40e2-a561-803865e8c7ca_Solar_community_timelapse_passin_20260917105216.mp4';
const POSTER_SRC =
  'https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/b26d5bc9261913cf996d81f1a879e2ea.png';

// Total scroll distance allocated to the 8s scrub. 600vh ≈ 6 screens of wheel.
const SCRUB_VH = 560;

const GLINT_POINTS = [
  { left: '57%', top: '18%' },
  { left: '66%', top: '18%' },
  { left: '78%', top: '22%' },
  { left: '24%', top: '60%' },
  { left: '40%', top: '62%' },
  { left: '65%', top: '58%' },
  { left: '70%', top: '60%' },
];

export default function Hero() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef(0);
  const targetRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const durationRef = useRef(8);
  const lastSeekRef = useRef(-1);
  const [progress, setProgress] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [glintKey, setGlintKey] = useState(0);
  const [glintIdx, setGlintIdx] = useState(0);

  useEffect(() => {
    let timer: number | null = null;
    const schedule = () => {
      const delay = 4000 + Math.random() * 3000;
      timer = window.setTimeout(() => {
        setGlintIdx((prev) => {
          let next = Math.floor(Math.random() * GLINT_POINTS.length);
          if (next === prev) next = (next + 1) % GLINT_POINTS.length;
          return next;
        });
        setGlintKey((k) => k + 1);
        schedule();
      }, delay);
    };
    const initial = window.setTimeout(() => {
      setGlintKey((k) => k + 1);
      schedule();
    }, 1100);
    return () => {
      window.clearTimeout(initial);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  // Prime the video so the first frame is decoded and shown
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => {
      if (v.duration && Number.isFinite(v.duration)) {
        durationRef.current = v.duration;
      }
      try {
        v.currentTime = 0.0001;
      } catch {
        /* noop */
      }
    };
    const onCanPlay = () => setVideoReady(true);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('loadeddata', onCanPlay);
    v.addEventListener('canplay', onCanPlay);
    return () => {
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('loadeddata', onCanPlay);
      v.removeEventListener('canplay', onCanPlay);
    };
  }, []);

  useEffect(() => {
    const clamp = (v: number, min = 0, max = 1) => Math.min(Math.max(v, min), max);

    // "Chase play" mode: scroll position defines a TARGET video time. Instead of
    // seeking directly (which skips intermediate frames), we let the video play
    // forward at a dynamic playbackRate to catch up. Reverse motion still has to
    // seek because HTML5 video does not support negative playbackRate.
    const syncVideo = () => {
      const v = videoRef.current;
      if (!v) return;
      const dur = durationRef.current || 8;
      const targetTime = Math.min(Math.max(targetRef.current * dur, 0), Math.max(dur - 0.001, 0));
      const current = v.currentTime;
      const gap = targetTime - current;

      // Close enough — hold the frame.
      if (Math.abs(gap) < 0.05) {
        if (!v.paused) v.pause();
        return;
      }

      if (gap > 0) {
        // Forward: fast-forward via playbackRate. Bigger gap = faster catch-up.
        const rate = Math.min(Math.max(gap * 6, 1), 12);
        if (v.playbackRate !== rate) v.playbackRate = rate;
        if (v.paused) {
          const p = v.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      } else {
        // Reverse: must seek. Step back in small chunks so it still feels animated.
        if (!v.paused) v.pause();
        const step = Math.max(gap, -0.5); // at most 0.5s per frame backwards
        const next = Math.max(current + step, targetTime);
        if (Math.abs(next - lastSeekRef.current) > 0.016) {
          lastSeekRef.current = next;
          try { v.currentTime = next; } catch { /* noop */ }
        }
      }
    };

    const animate = () => {
      syncVideo();
      const p = targetRef.current;
      progressRef.current = p;
      setProgress(p);
      window.dispatchEvent(new CustomEvent('hero-progress', { detail: p }));
      rafRef.current = requestAnimationFrame(animate);
    };

    const kick = () => {
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(animate);
    };
    // Keep the rAF loop running continuously so the video keeps chasing the target
    // even after the user stops scrolling.
    kick();

    const recompute = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      // Scrolled distance into the pinned range = -rect.top (once top passes viewport top)
      const scrolled = -rect.top;
      const p = clamp(scrolled / Math.max(total, 1));
      targetRef.current = p;
      kick();
    };

    recompute();
    window.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);

    return () => {
      window.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const clamp = (v: number, min = 0, max = 1) => Math.min(Math.max(v, min), max);

  const textDark = clamp(1 - (progress - 0.35) / 0.3);
  const c = Math.round(10 + (255 - 10) * (1 - textDark));
  const textColor = `rgb(${c}, ${c}, ${c})`;
  const subTextColor = `rgba(${c}, ${c}, ${c}, 0.85)`;

  return (
    <section ref={sectionRef} className="relative w-full" style={{ height: `${SCRUB_VH}vh` }}>
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
        {/* Poster — visible until the video's first frame is decoded */}
        <img
          src={POSTER_SRC}
          alt="Modern solar community in daylight"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ opacity: videoReady ? 0 : 1, transition: 'opacity 400ms ease' }}
        />
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover object-center will-change-transform"
          src={VIDEO_SRC}
          poster={POSTER_SRC}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
        />

        <div
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/10 via-transparent to-black/40"
          style={{ opacity: 0.35 + (1 - textDark) * 0.55 }}
        ></div>

        <div className="relative z-10 h-full flex flex-col items-center justify-center pt-16 pb-24 px-5 md:px-10">
          <div className="w-full max-w-[1080px] text-center -translate-y-[4vh]">
            <h1
              className="display-font font-medium tracking-tight text-[26px] sm:text-[52px] md:text-[62px] lg:text-[72px] leading-[1.1] sm:leading-[1.05] relative"
              style={{
                color: textColor,
                textShadow: textDark > 0.02
                  ? `0 -1px 0 rgba(255,255,255,${0.5 * textDark}), -1px -0.5px 0 rgba(255,255,255,${0.32 * textDark}), 1px 0 0 rgba(255,255,255,${0.16 * textDark}), 1px 1.5px 1.5px rgba(0,0,0,${0.16 * textDark})`
                  : 'none',
              }}
            >
              <span
                style={{
                  color: `rgb(${Math.round(17 + (3 - 17) * (1 - textDark))}, ${Math.round(17 + (217 - 17) * (1 - textDark))}, ${Math.round(17 + (89 - 17) * (1 - textDark))})`,
                  textShadow: 'none',
                }}
              >Clean energy</span> for the way
              <br />
              communities live.
              <span aria-hidden="true" className="pointer-events-none absolute inset-0">
                {textDark > 0.4 && glintKey > 0 && (
                  <span key={`glint-${glintKey}`} className="pointer-events-none absolute inset-0">
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute"
                      style={{
                        left: GLINT_POINTS[glintIdx].left,
                        top: GLINT_POINTS[glintIdx].top,
                        width: '90px',
                        height: '90px',
                        transform: 'translate(-50%, -50%)',
                        background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,240,200,0.55) 22%, rgba(255,215,140,0.15) 55%, rgba(255,215,140,0) 78%)',
                        filter: 'blur(1px)',
                        opacity: textDark,
                        animation: 'solarGlint 1600ms ease-out 1 forwards',
                        mixBlendMode: 'screen',
                        zIndex: 2,
                      }}
                    />
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute"
                      style={{
                        left: GLINT_POINTS[glintIdx].left,
                        top: GLINT_POINTS[glintIdx].top,
                        width: '12px',
                        height: '12px',
                        transform: 'translate(-50%, -50%)',
                        background: 'radial-gradient(circle, #ffffff 0%, rgba(255,255,255,0.9) 40%, rgba(255,255,255,0) 100%)',
                        borderRadius: '9999px',
                        opacity: 0,
                        animation: 'solarGlintCore 1600ms ease-out 1 forwards',
                        zIndex: 3,
                      }}
                    />
                  </span>
                )}
              </span>
            </h1>
            <p
              className="mt-6 mx-auto text-[13px] md:text-lg lg:text-[19px] leading-relaxed max-w-[620px]"
              style={{ color: subTextColor }}
            >
              Solar solutions for homes and businesses —
              <br className="hidden sm:block" />
              bright by day, reliable by night.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => scrollToHomeSection('estimate')}
                className="inline-flex items-center h-[44px] md:h-[52px] px-5 md:px-7 rounded-[10px] bg-solar-green text-solar-ink text-[13px] md:text-[15px] font-semibold whitespace-nowrap hover:bg-[#00c850] transition-colors cursor-pointer border-0"
              >
                Get My Estimate
              </button>
              <button
                type="button"
                onClick={() => scrollToHomeSection('solutions')}
                className="inline-flex items-center gap-2 h-[44px] md:h-[52px] px-3 text-[13px] md:text-[15px] font-medium whitespace-nowrap group cursor-pointer bg-transparent border-0"
                style={{ color: textColor }}
              >
                Explore Solutions
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
              </button>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 text-xs font-medium tracking-wide"
          style={{ color: subTextColor }}
        >
          <i className={`ri-sun-line text-base ${progress < 0.5 ? 'text-solar-yellow' : 'opacity-50'}`}></i>
          <div className="w-28 h-[2px] rounded-full overflow-hidden" style={{ backgroundColor: `rgba(${c},${c},${c},0.25)` }}>
            <div className="h-full transition-all duration-100" style={{ width: `${progress * 100}%`, backgroundColor: textColor }}></div>
          </div>
          <i className={`ri-moon-line text-base ${progress > 0.5 ? '' : 'opacity-50'}`}></i>
        </div>
      </div>
    </section>
  );
}